import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Box, ButtonBase } from "@mui/material";
import PushPinRoundedIcon from "@mui/icons-material/PushPinRounded";
import { boardTokens } from "@/shared/theme";
import { evidenceHighlightSquares } from "./evidence-highlights";
import {
  boundedCircularRadialLayout,
  collapsedEvidenceOffset,
  disclosureCorner,
  disclosureNeedsExternalDock,
  evidenceRadius,
  evidenceBadgeSize,
  needsEvidenceWheel,
  squareAtBoardPoint,
} from "./evidence-radial";

export type BoardEvidenceFocus = { fen: string; square: string };

export interface BoardEvidenceItem {
  id: string;
  kind: "motif" | "principle" | "critical" | "verified";
  square: string;
  label: string;
  color: string;
  description: string;
  glyph: ReactNode;
  disclosure: ReactNode;
  principleStatus?: "applied" | "missed";
  book?: boolean;
  moveScope?: string;
  relationship?: string;
  playable?: boolean;
  highlightSquares?: readonly string[];
  highlightFen?: string;
  onSelect?: () => void;
}
const disclosure = boardTokens.evidenceDisclosure;
const coordinates = (square: string, orientation: "white" | "black") => {
  const file = square.charCodeAt(0) - 97;
  const rank = Number(square[1]);
  return { column: orientation === "white" ? file : 7 - file, row: orientation === "white" ? 8 - rank : rank - 1 };
};

export function BoardEvidenceWheel({
  items,
  displayFen,
  orientation,
  bookOnly = false,
  onActiveChange,
}: {
  bookOnly?: boolean;
  items: BoardEvidenceItem[];
  displayFen: string;
  orientation: "white" | "black";
  onActiveChange?: (focus: BoardEvidenceFocus | null) => void;
}) {
  const [selection, setSelected] = useState<BoardEvidenceItem | null>(null);
  const [pinnedFen, setPinnedFen] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [boxPosition, setBoxPosition] = useState<{ left: number; top: number } | null>(null);
  const explanation = useRef<HTMLDivElement>(null);
  const drag = useRef<{ pointer: number; x: number; y: number; left: number; top: number } | null>(null);
  const [active, setActive] = useState<{ id: string; fen: string } | null>(null);
  const [expandedSquare, setExpandedSquare] = useState<string | null>(null);
  const [squareSize, setSquareSize] = useState(80);
  const BADGE_SIZE = evidenceBadgeSize(squareSize);
  const closeTimer = useRef<number | undefined>(undefined);
  const expandOnlyPress = useRef(false);
  const touchInteraction = useRef(false);
  const root = useRef<HTMLDivElement>(null);
  const groups = useMemo(() => {
    const bookScopes = new Set(items.filter((item) => item.book).map((item) => item.moveScope));
    const visible = items.filter((item) => item.book === true || (!bookOnly && !bookScopes.has(item.moveScope)));
    const merged = new Map<string, BoardEvidenceItem[]>();
    for (const item of visible) merged.set(item.square, [...(merged.get(item.square) ?? []), item]);
    return [...merged].map(([square, entries]) => ({ square, items: entries }));
  }, [items, bookOnly]);
  const currentSelection = groups.flatMap((group) => group.items).find((item) => item.id === selection?.id) ?? null;
  const selected = pinnedFen
    ? pinnedFen === displayFen
      ? (currentSelection ?? selection)
      : selection
    : currentSelection;
  const boundBox = useCallback((left: number, top: number) => {
    const board = root.current?.getBoundingClientRect();
    const box = explanation.current?.getBoundingClientRect();
    return {
      left: Math.max(8, Math.min(left, (board?.width ?? 0) - (box?.width ?? 0) - 8)),
      top: Math.max(8, Math.min(top, (board?.height ?? 0) - (box?.height ?? 0) - 8)),
    };
  }, []);
  useEffect(() => {
    if (!pinnedFen) {
      setBoxPosition(null);
      setDragging(false);
      return;
    }
    const board = root.current?.getBoundingClientRect();
    const box = explanation.current?.getBoundingClientRect();
    if (board && box) setBoxPosition((current) => current ?? boundBox(box.left - board.left, box.top - board.top));
  }, [pinnedFen, selection?.id, boundBox]);
  const activeItem =
    active?.fen === displayFen
      ? (groups.flatMap((group) => group.items).find((item) => item.id === active.id) ?? null)
      : null;
  const focusSquare =
    activeItem && (!activeItem.highlightFen || activeItem.highlightFen === displayFen) ? activeItem.square : null;
  useEffect(() => {
    onActiveChange?.(focusSquare ? { fen: displayFen, square: focusSquare } : null);
    return () => onActiveChange?.(null);
  }, [focusSquare, displayFen, onActiveChange]);
  const cancelClose = useCallback(() => {
    if (closeTimer.current != null) window.clearTimeout(closeTimer.current);
    closeTimer.current = undefined;
  }, []);
  const scheduleClose = useCallback(() => {
    if (pinnedFen || touchInteraction.current) return;
    if (closeTimer.current != null) return;
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = undefined;
      setSelected(null);
      setExpandedSquare(null);
    }, 320);
  }, [pinnedFen]);
  const openSquare = useCallback(
    (square: string, force = false) => {
      if (pinnedFen && !force) return;
      cancelClose();
      const group = groups.find((candidate) => candidate.square === square);
      setExpandedSquare(
        group &&
          needsEvidenceWheel(
            group.items.map((item) => item.kind),
            squareSize,
          )
          ? square
          : null,
      );
      setSelected((current) => (current?.square === square ? current : null));
    },
    [cancelClose, groups, squareSize, pinnedFen],
  );

  useEffect(() => {
    const node = root.current;
    if (!node) return;
    const measure = () => {
      const width = node.getBoundingClientRect().width;
      if (width > 0) setSquareSize(width / 8);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    setActive(null);
    setExpandedSquare(null);
  }, [displayFen, orientation]);
  useEffect(() => {
    if (!pinnedFen) setSelected(null);
  }, [displayFen, orientation, pinnedFen]);
  useEffect(() => {
    const visibleIds = new Set(groups.flatMap((group) => group.items.map((item) => item.id)));
    setActive((current) => (current && visibleIds.has(current.id) ? current : null));
    if (!pinnedFen) setSelected((current) => (current && visibleIds.has(current.id) ? current : null));
    setExpandedSquare((current) => (groups.some((group) => group.square === current) ? current : null));
  }, [groups, pinnedFen]);
  useEffect(() => {
    const pointerDown = (event: PointerEvent) => {
      touchInteraction.current = event.pointerType === "touch";
      if (root.current?.contains(event.target as Node)) return;
      setPinnedFen(null);
      setActive(null);
      setSelected(null);
      const rect = root.current?.getBoundingClientRect();
      const square = rect ? squareAtBoardPoint(rect, event.clientX, event.clientY, orientation) : null;
      // Keep the center clear throughout a press; collapsing between pointerdown
      // and click can move a badge over the piece and swallow a capture.
      if (square && groups.some((group) => group.square === square)) openSquare(square);
      else {
        setSelected(null);
        setExpandedSquare(null);
      }
    };
    const pointerMove = (event: PointerEvent) => {
      if (pinnedFen) return;
      if (event.pointerType && event.pointerType !== "mouse") return;
      touchInteraction.current = false;
      const interactive = (event.target as Element | null)?.closest?.<HTMLElement>("[data-evidence-interactive]");
      if (interactive) {
        cancelClose();
        const square = interactive.dataset.evidenceSquare;
        if (square) openSquare(square);
        return;
      }
      const rect = root.current?.getBoundingClientRect();
      const square = rect ? squareAtBoardPoint(rect, event.clientX, event.clientY, orientation) : null;
      const group = groups.find((candidate) => candidate.square === square);
      if (group) openSquare(group.square);
      else scheduleClose();
    };
    const keyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setPinnedFen(null);
      setActive(null);
      setSelected(null);
      setExpandedSquare(null);
    };
    document.addEventListener("keydown", keyDown);
    document.addEventListener("pointerdown", pointerDown);
    document.addEventListener("pointermove", pointerMove);
    return () => {
      document.removeEventListener("pointerdown", pointerDown);
      document.removeEventListener("keydown", keyDown);
      document.removeEventListener("pointermove", pointerMove);
      cancelClose();
    };
  }, [cancelClose, groups, openSquare, orientation, scheduleClose, squareSize, pinnedFen]);

  const selectedPosition = selected ? coordinates(selected.square, orientation) : { column: 0, row: 0 };
  const selectedCorner = disclosureCorner(selectedPosition.column, selectedPosition.row);
  const selectedGroup = selected ? groups.find((group) => group.square === selected.square) : null;
  const disclosureExternal =
    expandedSquare === selected?.square &&
    disclosureNeedsExternalDock(
      squareSize * 8,
      selectedPosition.column,
      selectedPosition.row,
      selectedGroup?.items.length ?? 0,
    );
  const tooltipId = selected ? `evidence-tooltip-${selected.square}` : undefined;
  return (
    <Box
      ref={root}
      data-board-evidence-root
      sx={{ position: "absolute", inset: 0, zIndex: 25, pointerEvents: "none", overflow: "visible" }}
    >
      {evidenceHighlightSquares(activeItem, displayFen).map((square) => {
        const { column, row } = coordinates(square, orientation);
        return (
          <Box
            key={square}
            data-evidence-highlight={square}
            aria-hidden
            sx={{
              position: "absolute",
              left: `${column * 12.5}%`,
              top: `${row * 12.5}%`,
              width: "12.5%",
              height: "12.5%",
              pointerEvents: "none",
              bgcolor: boardTokens.interaction.selected,
              opacity: 0.5,
              zIndex: 0,
            }}
          />
        );
      })}
      {groups.map(({ square, items }) => {
        const { column, row } = coordinates(square, orientation);
        const wheelEnabled = needsEvidenceWheel(
          items.map((item) => item.kind),
          squareSize,
        );
        const expanded = wheelEnabled && expandedSquare === square;
        const radius = evidenceRadius(squareSize, items.length);
        const positions = boundedCircularRadialLayout(
          items.length,
          radius,
          (column + 0.5) * squareSize,
          (row + 0.5) * squareSize,
          squareSize * 8,
          BADGE_SIZE,
        );
        return (
          <Box
            key={square}
            id={`evidence-group-${square}`}
            role="group"
            aria-label={`Board evidence at ${square}`}
            data-evidence-square-trigger={square}
            data-evidence-wheel-enabled={wheelEnabled}
            sx={{
              position: "absolute",
              pointerEvents: "none",
              left: `${column * 12.5}%`,
              top: `${row * 12.5}%`,
              width: "12.5%",
              aspectRatio: 1,
              // A disclosure must not intercept pointer-up on the badge that opened it.
              zIndex: expanded || selected?.square === square ? 61 : 26,
            }}
          >
            <Box
              data-evidence-radial-origin
              sx={{
                position: "absolute",
                left: "50%",
                top: "50%",
                width: BADGE_SIZE,
                height: BADGE_SIZE,
                transform: "translate(-50%,-50%)",
                zIndex: 30,
              }}
            >
              {items.map((item, index) => {
                const kindIndex = items.slice(0, index).filter((candidate) => candidate.kind === item.kind).length;
                const kindCount = items.filter((candidate) => candidate.kind === item.kind).length;
                const collapsed = collapsedEvidenceOffset(item.kind, squareSize, kindIndex, kindCount);
                const position = expanded ? positions[index] : collapsed;
                const color = item.color;
                const representative = kindIndex === 0;
                return (
                  <ButtonBase
                    key={item.id}
                    data-evidence-interactive
                    data-evidence-square={square}
                    data-evidence-radial-item={index}
                    data-evidence-kind={item.kind}
                    data-evidence-active={activeItem?.id === item.id}
                    aria-pressed={activeItem?.id === item.id}
                    data-evidence-visual={
                      item.kind === "motif" ? "motif" : item.kind === "principle" ? "principle" : "review"
                    }
                    data-collapsed-slot={item.kind}
                    {...(kindCount > 1 ? { "data-evidence-stack-card": true } : {})}
                    {...(item.kind === "principle" ? { "data-principle-status": item.principleStatus } : {})}
                    aria-label={item.label}
                    title={item.description}
                    {...(item.playable ? { "data-evidence-line-playable": true } : {})}
                    {...(wheelEnabled ? { "aria-expanded": expanded } : {})}
                    aria-describedby={selected?.id === item.id ? tooltipId : undefined}
                    tabIndex={expanded || representative ? 0 : -1}
                    onPointerDown={() => {
                      expandOnlyPress.current = wheelEnabled && !expanded;
                    }}
                    onFocus={() => {
                      if (pinnedFen) return;
                      openSquare(square);
                      setSelected(item);
                    }}
                    onMouseEnter={() => {
                      if (pinnedFen) return;
                      openSquare(square);
                      setSelected(item);
                    }}
                    onMouseLeave={scheduleClose}
                    onClick={(event) => {
                      event.stopPropagation();
                      cancelClose();
                      openSquare(square, true);
                      if (selection?.id !== item.id) setBoxPosition(null);
                      else {
                        const board = root.current?.getBoundingClientRect();
                        const box = explanation.current?.getBoundingClientRect();
                        if (board && box) setBoxPosition(boundBox(box.left - board.left, box.top - board.top));
                      }
                      setSelected(item);
                      setPinnedFen(displayFen);
                      if (!expandOnlyPress.current || item.onSelect) {
                        setActive((current) =>
                          current?.id === item.id && current.fen === displayFen
                            ? null
                            : { id: item.id, fen: displayFen },
                        );
                        item.onSelect?.();
                      }
                      expandOnlyPress.current = false;
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") {
                        setPinnedFen(null);
                        setSelected(null);
                        setExpandedSquare(null);
                      }
                    }}
                    sx={{
                      position: "absolute",
                      pointerEvents: "auto",
                      inset: 0,
                      width: BADGE_SIZE,
                      height: BADGE_SIZE,
                      minWidth: 0,
                      opacity: 1,
                      transform: `translate3d(${position.x}px,${position.y}px,0)`,
                      transformOrigin: "50% 50%",
                      transition: "transform 240ms cubic-bezier(.22,1,.36,1)",
                      willChange: "transform",
                      zIndex: 50 - index,
                      borderRadius: 0,
                      "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                      ...(item.kind === "motif"
                        ? { bgcolor: "transparent", border: 0, boxShadow: "none" }
                        : {
                            bgcolor: "#0a0a0a",
                            color,
                            border: "2px solid currentColor",
                            boxShadow: "2px 2px 0 #0a0a0a",
                          }),
                    }}
                  >
                    {item.glyph}
                  </ButtonBase>
                );
              })}
            </Box>
          </Box>
        );
      })}
      {selected && (
        <Box
          ref={explanation}
          id={tooltipId}
          role={pinnedFen ? "dialog" : "tooltip"}
          aria-modal={pinnedFen ? false : undefined}
          aria-label={pinnedFen ? `${selected.label} explanation` : undefined}
          style={pinnedFen && boxPosition ? { ...boxPosition, right: "auto", bottom: "auto" } : undefined}
          data-evidence-interactive
          data-evidence-square={selected.square}
          data-disclosure-corner={selectedCorner}
          data-disclosure-layout={disclosureExternal ? "external" : "corner"}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          onPointerDown={(event) => {
            if (!pinnedFen || event.button !== 0) return;
            const target = event.target as Element;
            if (
              !target.closest("[data-explanation-drag-handle]") &&
              target.closest(
                'button, a, input, select, textarea, label, summary, [role="button"], [role="link"], [role="slider"], [role="tab"], [role="checkbox"], [role="switch"], [tabindex], [contenteditable="true"], [data-no-drag]',
              )
            )
              return;
            event.preventDefault();
            event.stopPropagation();
            const board = root.current?.getBoundingClientRect();
            const box = explanation.current?.getBoundingClientRect();
            if (!board || !box) return;
            setDragging(true);
            drag.current = {
              pointer: event.pointerId,
              x: event.clientX,
              y: event.clientY,
              left: box.left - board.left,
              top: box.top - board.top,
            };
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerMove={(event) => {
            const press = drag.current;
            if (press?.pointer !== event.pointerId) return;
            event.stopPropagation();
            setBoxPosition(boundBox(press.left + event.clientX - press.x, press.top + event.clientY - press.y));
          }}
          onPointerUp={(event) => {
            drag.current = null;
            setDragging(false);
            if (event.currentTarget.hasPointerCapture(event.pointerId))
              event.currentTarget.releasePointerCapture(event.pointerId);
          }}
          onPointerCancel={() => {
            drag.current = null;
            setDragging(false);
          }}
          onLostPointerCapture={() => {
            drag.current = null;
            setDragging(false);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              setPinnedFen(null);
              setSelected(null);
              setExpandedSquare(null);
            }
          }}
          sx={{
            position: "absolute",
            pointerEvents: "auto",
            cursor: pinnedFen ? (dragging ? "grabbing" : "grab") : undefined,
            touchAction: pinnedFen ? "none" : undefined,
            zIndex: 60,
            display: "flex",
            flexDirection: "column",
            overflow: "visible",
            maxHeight: pinnedFen ? Math.min(disclosure.maxHeight, squareSize * 8 - 16) : disclosure.maxHeight,
            maxWidth: "calc(100% - 16px)",
            boxSizing: "border-box",
            ...(disclosureExternal
              ? {
                  top: "calc(100% + 12px)",
                  width: "min(280px, calc(100% - 24px))",
                  minHeight: 176,
                  ...(selectedCorner.endsWith("left") ? { left: 12 } : { right: 12 }),
                }
              : {
                  width: `clamp(${disclosure.minWidth}px, calc(50% - ${disclosure.inset * 2}px), ${disclosure.maxWidth}px)`,
                  minHeight: `clamp(${disclosure.minHeight}px, calc(50% - ${disclosure.inset * 2}px), ${disclosure.maxHeight}px)`,
                  ...(selectedCorner.startsWith("top") ? { top: 12 } : { bottom: 12 }),
                  ...(selectedCorner.endsWith("left") ? { left: 12 } : { right: 12 }),
                }),
            bgcolor: "#0a0a0a",
            color: "primary.main",
            border: "2px solid",
            borderColor: selected.color,
            boxShadow: dragging ? "6px 6px 0 rgba(0,0,0,.55)" : "none",
            ...(selected.kind === "motif" && {
              "& .MuiTypography-subtitle1": { textTransform: "uppercase" },
            }),
          }}
        >
          {pinnedFen && (
            <ButtonBase
              aria-label="Move explanation"
              data-explanation-drag-handle
              aria-description="Drag or use arrow keys to move this explanation."
              onKeyDown={(event) => {
                const delta = (
                  { ArrowLeft: [-10, 0], ArrowRight: [10, 0], ArrowUp: [0, -10], ArrowDown: [0, 10] } as Record<
                    string,
                    [number, number]
                  >
                )[event.key];
                if (!delta || !boxPosition) return;
                event.preventDefault();
                event.stopPropagation();
                setBoxPosition(boundBox(boxPosition.left + delta[0], boxPosition.top + delta[1]));
              }}
              sx={{
                display: "flex",
                position: "absolute",
                top: -18,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 1,
                width: 44,
                height: 36,
                cursor: dragging ? "grabbing" : "grab",
                touchAction: "none",
                userSelect: "none",
                color: selected.color,
                "&.Mui-focusVisible": { outline: "2px solid currentColor", outlineOffset: 2 },
                "& svg": {
                  fontSize: 23,
                  stroke: "#0a0a0a",
                  strokeWidth: 2,
                  paintOrder: "stroke fill",
                  strokeLinejoin: "round",
                  transform: dragging ? "rotate(15deg) translateY(-4px)" : "rotate(15deg) translateY(2px)",
                  clipPath: dragging ? "inset(0)" : "inset(0 0 24%)",
                  transition: "transform 120ms ease-out, clip-path 120ms ease-out",
                },
                "@media (prefers-reduced-motion: reduce)": { "& svg": { transition: "none" } },
              }}
            >
              <PushPinRoundedIcon />
            </ButtonBase>
          )}
          <Box sx={{ p: 1.5, minHeight: 0, overflowY: "auto" }}>
            {pinnedFen && pinnedFen !== displayFen && (
              <Box sx={{ fontSize: ".6rem", color: "text.secondary", mb: 0.75 }}>Selected position</Box>
            )}
            {selected.disclosure}
          </Box>
        </Box>
      )}
    </Box>
  );
}
